import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Check, AlertTriangle, Clock, Pill, ShieldCheck, Scissors, Camera, Download } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const translations = {
  es: {
    eyebrow: 'Cirugía Corporal',
    title: 'Braquioplastia',
    subtitle: 'Lifting de Brazos',
    heroDesc: 'La braquioplastia es un procedimiento seguro que elimina el exceso de piel del brazo, devolviendo un contorno firme y definido.',
    heroNote: 'Las imágenes corresponden a casos reales y los resultados varían según cada paciente.',
    requestConsult: 'Solicitar consulta',
    callNow: 'Llamar ahora',
    downloadPdf: 'Descargar información (PDF)',
    pdfUrl: '/docs/Braquioplastia_DrNorro.pdf',
    expLabel: 'Trayectoria',
    expTitle: 'Más de 20 años de experiencia',
    expDesc: 'Documentación quirúrgica propia de nuestros primeros casos de braquioplastia. La fecha impresa en las imágenes —2007— es testimonio de una trayectoria de más de dos décadas en cirugía del contorno del brazo.',
    expTech: 'Técnica clásica + tecnología actual: a la cirugía tradicional incorporamos el moderno tensado de la piel mediante endolifting láser infrarrojo, que potencia la retracción cutánea y refina el resultado del contorno del brazo.',
    resultsLabel: 'Casos reales',
    resultsTitle: 'Galería · Antes y Después',
    before: 'Antes',
    after: 'Después',
    preLabel: 'Cuidados · Antes del procedimiento',
    preTitle: 'Preparación preoperatoria',
    arrivalTitle: 'Llegada y ayuno',
    arrivalItems: [
      'Acuda puntualmente a la clínica.',
      'Con sedación o anestesia general: no coma ni beba nada durante las 8-10 horas previas.',
      'Sin sedación (solo anestesia local): no es necesario el ayuno.',
      'Si recibe sedación o anestesia general, venga acompañado y no conduzca después.',
      'Durante las 24 h posteriores, evite manejar maquinaria o tomar decisiones importantes.',
    ],
    prepTitle: 'Preparación personal',
    prepItems: [
      'Ropa cómoda y holgada con apertura delantera (botones o cremallera).',
      'Acuda con la piel limpia, sin cremas, desodorante ni joyas.',
      'Uñas sin esmalte (permite el control con pulsioxímetro si hay sedación).',
    ],
    medsTitle: 'Medicamentos',
    medsItems: [
      'Evite antiinflamatorios (ibuprofeno, aspirina, naproxeno) los 10 días previos.',
      'Comente con el equipo cualquier anticoagulante o antiagregante que tome.',
      'Puede tomar paracetamol si necesita aliviar molestias.',
      'Evite el tabaco y el alcohol al menos 2-3 semanas antes.',
    ],
    homeItems: 'Prepare en casa: bolsas de frío / gel frío y almohadas para mantener los brazos elevados.',
    homeNote: 'El resto del material (prenda de compresión, apósitos, suero, antiséptico, medicación y crema cicatrizante) se le proporciona en la clínica.',
    postLabel: 'Cuidados · Después del procedimiento',
    postTitle: 'Recuperación postoperatoria',
    firstDaysTitle: 'Primeros días en casa',
    firstDaysItems: [
      'Mantenga los brazos elevados sobre almohadas para reducir la hinchazón, sobre todo los primeros 3-4 días.',
      'Mantenga la prenda de compresión o vendaje elástico de forma continua.',
      'Aplique frío local 10-15 min cada 2 horas los primeros 3 días.',
      'Cierta hinchazón, hematoma y tirantez es normal.',
    ],
    medPostTitle: 'Medicación',
    medPostItems: [
      'Si tiene molestias: paracetamol 500 mg cada 8 horas.',
      'Tome antibióticos solo si se los han prescrito.',
      'Reinicie antiinflamatorios solo cuando su médico se lo indique.',
    ],
    followTitle: 'Seguimiento',
    followItems: [
      'Envíe una foto diaria por WhatsApp los primeros días.',
      'Los puntos se retiran entre el 12.º y el 15.º día.',
      'Sutura limpia y seca, aplique povidona yodada 1 vez/día.',
    ],
    avoidTitle: 'Actividades a evitar (mín. 2-3 semanas)',
    avoidItems: [
      'Levantar peso o hacer esfuerzos con los brazos.',
      'Elevar los brazos por encima de los hombros bruscamente.',
      'Ejercicio intenso y sudoración excesiva.',
      'Conducir hasta que su médico lo autorice.',
      'Exposición de la cicatriz al sol.',
      'Tabaco.',
    ],
    scarTitle: 'Cuidado de la cicatriz',
    scarItems: [
      'Tras retirar los puntos: crema cicatrizante 1-2 veces/día durante 2-3 meses.',
      'Pueden añadirse láminas o gel de silicona.',
      'Masaje suave de la cicatriz cuando su médico lo indique.',
      'Fotoprotección total (SPF 50+) sobre la cicatriz al menos 12 meses.',
    ],
    alertTitle: 'Contacte con nosotros si presenta',
    alertItems: [
      'Dolor intenso o creciente que no cede con la medicación.',
      'Sangrado que no se detiene o hematoma que aumenta.',
      'Enrojecimiento, calor, secreción o mal olor en la herida.',
      'Fiebre superior a 38 ºC.',
      'Hinchazón brusca, dura y dolorosa del brazo.',
    ],
    ctaTitle: 'Recupere un contorno firme y definido',
    ctaDesc: 'Solicite su consulta informativa sin compromiso. Le atendemos de forma personalizada.',
  },
  en: {
    eyebrow: 'Body Surgery',
    title: 'Brachioplasty',
    subtitle: 'Arm Lift',
    heroDesc: 'Brachioplasty is a safe procedure that removes excess arm skin, restoring a firm and defined contour.',
    heroNote: 'Images correspond to real cases and results vary according to each patient.',
    requestConsult: 'Request consultation',
    callNow: 'Call now',
    downloadPdf: 'Download information (PDF)',
    pdfUrl: '/docs/Brachioplasty_DrNorro_EN.pdf',
    expLabel: 'Track record',
    expTitle: 'Over 20 years of experience',
    expDesc: 'Our own surgical documentation from our first brachioplasty cases. The date printed on the images —2007— is testimony to over two decades of experience in arm contour surgery.',
    expTech: 'Classic technique + current technology: we incorporate modern skin tightening through infrared laser endolifting into traditional surgery, enhancing skin retraction and refining the arm contour result.',
    resultsLabel: 'Real cases',
    resultsTitle: 'Gallery · Before and After',
    before: 'Before',
    after: 'After',
    caption1: 'Front view · arms in adduction',
    caption2: 'Front view · arms elevated',
    caption3: 'Arm profile',
    anglesLabel: 'Different angles',
    anglesTitle: 'Profile views',
    preLabel: 'Care · Before the procedure',
    preTitle: 'Pre-operative preparation',
    arrivalTitle: 'Arrival and fasting',
    arrivalItems: [
      'Arrive at the clinic on time.',
      'With sedation or general anesthesia: do not eat or drink for 8-10 hours before.',
      'Without sedation (local anesthesia only): fasting is not necessary.',
      'If receiving sedation or general anesthesia, come accompanied and do not drive afterwards.',
      'For the following 24 hours, avoid operating machinery or making important decisions.',
    ],
    prepTitle: 'Personal preparation',
    prepItems: [
      'Comfortable, loose clothing with front opening (buttons or zipper).',
      'Come with clean skin, without creams, deodorant or jewelry.',
      'Nails without polish (allows pulse oximeter monitoring with sedation).',
    ],
    medsTitle: 'Medications',
    medsItems: [
      'Avoid anti-inflammatories (ibuprofen, aspirin, naproxen) for 10 days prior.',
      'Discuss any anticoagulant or antiplatelet medication with the team.',
      'You may take paracetamol if you need pain relief.',
      'Avoid tobacco and alcohol at least 2-3 weeks before.',
    ],
    homeItems: 'Prepare at home: cold packs / cold gel and pillows to keep arms elevated.',
    homeNote: 'The rest of the material (compression garment, dressings, saline, antiseptic, medication and scar cream) is provided at the clinic.',
    postLabel: 'Care · After the procedure',
    postTitle: 'Post-operative recovery',
    firstDaysTitle: 'First days at home',
    firstDaysItems: [
      'Keep arms elevated on pillows to reduce swelling, especially the first 3-4 days.',
      'Keep the compression garment or elastic bandage on continuously.',
      'Apply local cold 10-15 min every 2 hours for the first 3 days.',
      'Some swelling, bruising and tightness is normal.',
    ],
    medPostTitle: 'Medication',
    medPostItems: [
      'For discomfort: paracetamol 500 mg every 8 hours.',
      'Take antibiotics only if prescribed.',
      'Resume anti-inflammatories only when your doctor indicates.',
    ],
    followTitle: 'Follow-up',
    followItems: [
      'Send a daily photo via WhatsApp for the first few days.',
      'Stitches are removed between day 12 and 15.',
      'Keep suture clean and dry, apply povidone-iodine once/day.',
    ],
    avoidTitle: 'Activities to avoid (min. 2-3 weeks)',
    avoidItems: [
      'Lifting weight or straining the arms.',
      'Raising arms above shoulders abruptly.',
      'Intense exercise and excessive sweating.',
      'Driving until your doctor authorizes it.',
      'Exposing the scar to the sun.',
      'Tobacco.',
    ],
    scarTitle: 'Scar care',
    scarItems: [
      'After stitch removal: scar cream 1-2 times/day for 2-3 months.',
      'Silicone sheets or gel may be added.',
      'Gentle scar massage when your doctor indicates.',
      'Full sun protection (SPF 50+) on the scar for at least 12 months.',
    ],
    alertTitle: 'Contact us if you experience',
    alertItems: [
      'Intense or increasing pain not relieved by medication.',
      'Bleeding that does not stop or increasing hematoma.',
      'Redness, warmth, discharge or bad odor from the wound.',
      'Fever above 38 ºC.',
      'Sudden, hard and painful arm swelling.',
    ],
    ctaTitle: 'Restore a firm and defined contour',
    ctaDesc: 'Request your informative consultation with no commitment. We provide personalized care.',
  },
  fr: {
    eyebrow: 'Chirurgie Corporelle',
    title: 'Brachioplastie',
    subtitle: 'Lifting des bras',
    heroDesc: 'La brachioplastie est une procédure sûre qui élimine l\'excès de peau du bras, restaurant un contour ferme et défini.',
    heroNote: 'Les images correspondent à des cas réels et les résultats varient selon chaque patient.',
    requestConsult: 'Demander une consultation',
    callNow: 'Appeler maintenant',
    downloadPdf: 'Télécharger l\'information (PDF)',
    pdfUrl: '/docs/Braquioplastia_DrNorro.pdf',
    expLabel: 'Parcours',
    expTitle: 'Plus de 20 ans d\'expérience',
    expDesc: 'Documentation chirurgicale propre de nos premiers cas de brachioplastie. La date imprimée sur les images —2007— témoigne de plus de deux décennies d\'expérience en chirurgie du contour du bras.',
    expTech: 'Technique classique + technologie actuelle : nous intégrons le tensage moderne de la peau par endolifting laser infrarouge à la chirurgie traditionnelle, améliorant la rétraction cutanée et affinant le résultat.',
    resultsLabel: 'Cas réels',
    resultsTitle: 'Galerie · Avant et Après',
    before: 'Avant',
    after: 'Après',
    caption1: 'Vue frontale · bras en adduction',
    caption2: 'Vue frontale · bras élevés',
    caption3: 'Profil du bras',
    anglesLabel: 'Différents angles',
    anglesTitle: 'Vues de profil',
    preLabel: 'Soins · Avant la procédure',
    preTitle: 'Préparation préopératoire',
    arrivalTitle: 'Arrivée et jeûne',
    arrivalItems: [
      'Arrivez à la clinique à l\'heure.',
      'Avec sédation ou anesthésie générale : ne mangez ni ne buvez rien pendant les 8-10 heures précédentes.',
      'Sans sédation (anesthésie locale uniquement) : le jeûne n\'est pas nécessaire.',
      'Si vous recevez une sédation, venez accompagné et ne conduisez pas après.',
      'Pendant les 24 h suivantes, évitez de conduire des machines ou de prendre des décisions importantes.',
    ],
    prepTitle: 'Préparation personnelle',
    prepItems: [
      'Vêtements confortables et amples avec ouverture frontale.',
      'Venez avec la peau propre, sans crèmes, déodorant ni bijoux.',
      'Ongles sans vernis (permet le contrôle par oxymètre de pouls).',
    ],
    medsTitle: 'Médicaments',
    medsItems: [
      'Évitez les anti-inflammatoires les 10 jours précédents.',
      'Signalez tout anticoagulant ou antiagrégant que vous prenez.',
      'Vous pouvez prendre du paracétamol si nécessaire.',
      'Évitez le tabac et l\'alcool au moins 2-3 semaines avant.',
    ],
    homeItems: 'Préparez à la maison : poches de froid et coussins pour garder les bras élevés.',
    homeNote: 'Le reste du matériel est fourni à la clinique.',
    postLabel: 'Soins · Après la procédure',
    postTitle: 'Récupération postopératoire',
    firstDaysTitle: 'Premiers jours à la maison',
    firstDaysItems: [
      'Gardez les bras élevés sur des coussins les 3-4 premiers jours.',
      'Gardez le vêtement de compression en continu.',
      'Appliquez du froid local 10-15 min toutes les 2 heures les 3 premiers jours.',
      'Un certain gonflement et des ecchymoses sont normaux.',
    ],
    medPostTitle: 'Médication',
    medPostItems: [
      'En cas de gêne : paracétamol 500 mg toutes les 8 heures.',
      'Prenez des antibiotiques uniquement s\'ils sont prescrits.',
      'Reprenez les anti-inflammatoires uniquement sur indication médicale.',
    ],
    followTitle: 'Suivi',
    followItems: [
      'Envoyez une photo quotidienne par WhatsApp les premiers jours.',
      'Les points sont retirés entre le 12e et le 15e jour.',
      'Suture propre et sèche, appliquez de la povidone iodée 1 fois/jour.',
    ],
    avoidTitle: 'Activités à éviter (min. 2-3 semaines)',
    avoidItems: [
      'Soulever des poids ou faire des efforts avec les bras.',
      'Lever les bras au-dessus des épaules brusquement.',
      'Exercice intense et transpiration excessive.',
      'Conduire jusqu\'à autorisation médicale.',
      'Exposition de la cicatrice au soleil.',
      'Tabac.',
    ],
    scarTitle: 'Soin de la cicatrice',
    scarItems: [
      'Après retrait des points : crème cicatrisante 1-2 fois/jour pendant 2-3 mois.',
      'Des feuilles ou du gel de silicone peuvent être ajoutés.',
      'Massage doux de la cicatrice sur indication médicale.',
      'Protection solaire totale (SPF 50+) sur la cicatrice pendant au moins 12 mois.',
    ],
    alertTitle: 'Contactez-nous si vous présentez',
    alertItems: [
      'Douleur intense ou croissante non soulagée par la médication.',
      'Saignement persistant ou hématome croissant.',
      'Rougeur, chaleur, écoulement ou mauvaise odeur de la plaie.',
      'Fièvre supérieure à 38 ºC.',
      'Gonflement brusque, dur et douloureux du bras.',
    ],
    ctaTitle: 'Retrouvez un contour ferme et défini',
    ctaDesc: 'Demandez votre consultation informative sans engagement.',
  },
  pt: {
    eyebrow: 'Cirurgia Corporal',
    title: 'Braquioplastia',
    subtitle: 'Lifting de Braços',
    heroDesc: 'A braquioplastia é um procedimento seguro que elimina o excesso de pele do braço, devolvendo um contorno firme e definido.',
    heroNote: 'As imagens correspondem a casos reais e os resultados variam segundo cada paciente.',
    requestConsult: 'Solicitar consulta',
    callNow: 'Ligar agora',
    downloadPdf: 'Descarregar informação (PDF)',
    pdfUrl: '/docs/Braquioplastia_DrNorro.pdf',
    expLabel: 'Trajetória',
    expTitle: 'Mais de 20 anos de experiência',
    expDesc: 'Documentação cirúrgica própria dos nossos primeiros casos de braquioplastia. A data impressa nas imagens —2007— é testemunho de mais de duas décadas de experiência em cirurgia do contorno do braço.',
    expTech: 'Técnica clássica + tecnologia atual: incorporamos o moderno tensionamento da pele através de endolifting laser infravermelho à cirurgia tradicional, potenciando a retração cutânea e refinando o resultado.',
    resultsLabel: 'Casos reais',
    resultsTitle: 'Galeria · Antes e Depois',
    before: 'Antes',
    after: 'Depois',
    caption1: 'Vista frontal · braços em adução',
    caption2: 'Vista frontal · braços elevados',
    caption3: 'Perfil do braço',
    anglesLabel: 'Diferentes ângulos',
    anglesTitle: 'Vistas de perfil',
    preLabel: 'Cuidados · Antes do procedimento',
    preTitle: 'Preparação pré-operatória',
    arrivalTitle: 'Chegada e jejum',
    arrivalItems: [
      'Chegue pontualmente à clínica.',
      'Com sedação ou anestesia geral: não coma nem beba nada durante as 8-10 horas anteriores.',
      'Sem sedação (apenas anestesia local): o jejum não é necessário.',
      'Se receber sedação, venha acompanhado e não conduza depois.',
      'Durante as 24 h seguintes, evite conduzir máquinas ou tomar decisões importantes.',
    ],
    prepTitle: 'Preparação pessoal',
    prepItems: [
      'Roupa confortável e folgada com abertura frontal.',
      'Venha com a pele limpa, sem cremes, desodorizante nem joias.',
      'Unhas sem verniz (permite o controlo com oxímetro de pulso).',
    ],
    medsTitle: 'Medicamentos',
    medsItems: [
      'Evite anti-inflamatórios os 10 dias anteriores.',
      'Comunique qualquer anticoagulante que tome.',
      'Pode tomar paracetamol se necessitar.',
      'Evite tabaco e álcool pelo menos 2-3 semanas antes.',
    ],
    homeItems: 'Prepare em casa: bolsas de frio e almofadas para manter os braços elevados.',
    homeNote: 'O restante material é fornecido na clínica.',
    postLabel: 'Cuidados · Após o procedimento',
    postTitle: 'Recuperação pós-operatória',
    firstDaysTitle: 'Primeiros dias em casa',
    firstDaysItems: [
      'Mantenha os braços elevados sobre almofadas nos primeiros 3-4 dias.',
      'Mantenha a faixa de compressão de forma contínua.',
      'Aplique frio local 10-15 min a cada 2 horas nos primeiros 3 dias.',
      'Algum inchaço e hematoma são normais.',
    ],
    medPostTitle: 'Medicação',
    medPostItems: [
      'Se tiver dor: paracetamol 500 mg a cada 8 horas.',
      'Tome antibióticos apenas se prescritos.',
      'Retome anti-inflamatórios apenas quando o médico indicar.',
    ],
    followTitle: 'Acompanhamento',
    followItems: [
      'Envie uma foto diária por WhatsApp nos primeiros dias.',
      'Os pontos são retirados entre o 12.º e o 15.º dia.',
      'Sutura limpa e seca, aplique povidona iodada 1 vez/dia.',
    ],
    avoidTitle: 'Atividades a evitar (mín. 2-3 semanas)',
    avoidItems: [
      'Levantar peso ou fazer esforços com os braços.',
      'Elevar os braços acima dos ombros bruscamente.',
      'Exercício intenso e transpiração excessiva.',
      'Conduzir até autorização médica.',
      'Exposição da cicatriz ao sol.',
      'Tabaco.',
    ],
    scarTitle: 'Cuidado da cicatriz',
    scarItems: [
      'Após retirar os pontos: creme cicatrizante 1-2 vezes/dia durante 2-3 meses.',
      'Podem ser adicionadas folhas ou gel de silicone.',
      'Massagem suave da cicatriz quando o médico indicar.',
      'Proteção solar total (SPF 50+) na cicatriz durante pelo menos 12 meses.',
    ],
    alertTitle: 'Contacte-nos se apresentar',
    alertItems: [
      'Dor intensa ou crescente não aliviada pela medicação.',
      'Sangramento persistente ou hematoma crescente.',
      'Vermelhidão, calor, secreção ou mau odor na ferida.',
      'Febre superior a 38 ºC.',
      'Inchaço brusco, duro e doloroso do braço.',
    ],
    ctaTitle: 'Recupere um contorno firme e definido',
    ctaDesc: 'Solicite a sua consulta informativa sem compromisso.',
  },
};

const Braquioplastia = () => {
  const lang = useLanguage();
  const t = translations[lang] || translations.es;
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const CareCard = ({ icon: Icon, title, items }) => (
    <div className="bg-white p-6 border-t-2 border-[#B8975A]">
      <div className="flex items-center gap-3 mb-4">
        <Icon size={20} className="text-[#B8975A]" />
        <h4 className="font-serif text-lg text-[#1C1C1A]">{title}</h4>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#4A4845] leading-relaxed">
            <Check size={14} className="text-[#B8975A] mt-0.5 flex-shrink-0" /> {item}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeUp">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.eyebrow}</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1C1C1A] mb-2 leading-tight">{t.title}</h1>
              <p className="font-serif text-xl text-[#B8975A] italic mb-6">{t.subtitle}</p>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-base md:text-lg text-[#4A4845] leading-relaxed mb-4">{t.heroDesc}</p>
              <p className="text-xs text-[#8A8780] italic mb-8">{t.heroNote}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
                  {t.requestConsult} <ArrowRight size={16} />
                </Link>
                <a href="tel:+34952764547" className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors">
                  <Phone size={16} /> {t.callNow}
                </a>
              </div>
              {t.pdfUrl && (
                <a href={t.pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm text-[#B8975A] hover:text-[#96784a] transition-colors">
                  <Download size={16} /> {t.downloadPdf}
                </a>
              )}
            </AnimatedSection>
            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#B8975A]"></div>
                <img src="/images/braquio-antes-frontal.png" alt={t.title} className="w-full h-auto bg-white" />
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#B8975A]"></div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Experience + 2007 surgical photos */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-5xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.expLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">{t.expTitle}</h2>
            <p className="text-white/70 text-sm leading-relaxed max-w-3xl mx-auto mb-4">{t.expDesc}</p>
            <p className="text-white/70 text-sm leading-relaxed max-w-3xl mx-auto">{t.expTech}</p>
          </AnimatedSection>
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="text-center">
                <div className="border border-[#B8975A]/30 p-1.5">
                  <img src="/images/braquio-2007-marcaje.jpg" alt="Marcaje prequirúrgico 2007" className="w-full h-auto" />
                </div>
                <p className="text-white/60 text-xs mt-3">Marcaje prequirúrgico · 2007</p>
              </div>
              <div className="text-center">
                <div className="border border-[#B8975A]/30 p-1.5">
                  <img src="/images/braquio-2007-laxitud.jpg" alt="Valoración de laxitud 2007" className="w-full h-auto" />
                </div>
                <p className="text-white/60 text-xs mt-3">Valoración de la laxitud · 2007</p>
              </div>
              <div className="text-center">
                <div className="border border-[#B8975A]/30 p-1.5">
                  <img src="/images/braquio-2007-intraop.jpg" alt="Técnica quirúrgica 2007" className="w-full h-auto" />
                </div>
                <p className="text-white/60 text-xs mt-3">Técnica quirúrgica · 2007</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Results Gallery */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-5xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.resultsLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A]">{t.resultsTitle}</h2>
          </AnimatedSection>

          {/* Caso 1 */}
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <p className="text-center text-xs tracking-[0.15em] uppercase text-[#8A8780] mb-4">Caso 1</p>
            <div className="grid grid-cols-2 gap-4 mb-10">
              <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                <img src="/images/braquio-caso2-1.png" alt="Caso 1 - Vista 1" className="w-full h-auto" />
              </div>
              <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                <img src="/images/braquio-caso2-2.png" alt="Caso 1 - Vista 2" className="w-full h-auto" />
              </div>
            </div>
          </AnimatedSection>

          {/* Caso 2 */}
          <AnimatedSection animation="fadeUp" delay={0.15}>
            <p className="text-center text-xs tracking-[0.15em] uppercase text-[#8A8780] mb-4">Caso 2</p>
            <div className="grid grid-cols-2 gap-4 mb-10">
              <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                <img src="/images/braquio-caso3-1.png" alt="Caso 2 - Vista 1" className="w-full h-auto" />
              </div>
              <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                <img src="/images/braquio-caso3-2.png" alt="Caso 2 - Vista 2" className="w-full h-auto" />
              </div>
            </div>
          </AnimatedSection>

          {/* Caso 3 */}
          <AnimatedSection animation="fadeUp" delay={0.2}>
            <p className="text-center text-xs tracking-[0.15em] uppercase text-[#8A8780] mb-4">Caso 3</p>
            <div className="grid grid-cols-2 gap-4 mb-10">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#B8975A] mb-2 text-center font-medium">{t.before}</p>
                <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                  <img src="/images/braquio-caso5-antes.png" alt={`${t.before} - Caso 3`} className="w-full h-auto" />
                </div>
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#B8975A] mb-2 text-center font-medium">{t.after}</p>
                <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                  <img src="/images/braquio-caso5-despues.png" alt={`${t.after} - Caso 3`} className="w-full h-auto" />
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Caso 4 */}
          <AnimatedSection animation="fadeUp" delay={0.25}>
            <p className="text-center text-xs tracking-[0.15em] uppercase text-[#8A8780] mb-4">Caso 4</p>
            <div className="grid grid-cols-2 gap-4 mb-10">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#B8975A] mb-2 text-center font-medium">{t.before}</p>
                <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                  <img src="/images/braquio-caso6-antes.png" alt={`${t.before} - Caso 4`} className="w-full h-auto" />
                </div>
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#B8975A] mb-2 text-center font-medium">{t.after}</p>
                <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                  <img src="/images/braquio-caso6-despues.png" alt={`${t.after} - Caso 4`} className="w-full h-auto" />
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Caso 5 */}
          <AnimatedSection animation="fadeUp" delay={0.3}>
            <p className="text-center text-xs tracking-[0.15em] uppercase text-[#8A8780] mb-4">Caso 5</p>
            <div className="grid grid-cols-2 gap-4 mb-10">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#B8975A] mb-2 text-center font-medium">{t.before}</p>
                <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                  <img src="/images/braquio-caso7-antes.png" alt={`${t.before} - Caso 5`} className="w-full h-auto" />
                </div>
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#B8975A] mb-2 text-center font-medium">{t.after}</p>
                <div className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                  <img src="/images/braquio-caso7-despues.png" alt={`${t.after} - Caso 5`} className="w-full h-auto" />
                </div>
              </div>
            </div>
          </AnimatedSection>


        </div>
      </section>

      {/* Pre-op Care */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-5xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.preLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A]">{t.preTitle}</h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-6">
            <CareCard icon={Clock} title={t.arrivalTitle} items={t.arrivalItems} />
            <CareCard icon={ShieldCheck} title={t.prepTitle} items={t.prepItems} />
            <CareCard icon={Pill} title={t.medsTitle} items={t.medsItems} />
            <div className="bg-white p-6 border-t-2 border-[#B8975A]">
              <h4 className="font-serif text-lg text-[#1C1C1A] mb-4">Material</h4>
              <p className="text-sm text-[#4A4845] leading-relaxed mb-3">{t.homeItems}</p>
              <p className="text-xs text-[#8A8780] italic border-l-2 border-[#B8975A] pl-3">{t.homeNote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Post-op Care */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-5xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.postLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">{t.postTitle}</h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-[#B8975A]/30 p-6">
              <h4 className="font-serif text-lg text-[#B8975A] mb-4">{t.firstDaysTitle}</h4>
              <ul className="space-y-2">
                {t.firstDaysItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/70"><Check size={14} className="text-[#B8975A] mt-0.5 flex-shrink-0" /> {item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white/5 border border-[#B8975A]/30 p-6">
              <h4 className="font-serif text-lg text-[#B8975A] mb-4">{t.followTitle}</h4>
              <ul className="space-y-2 mb-6">
                {t.followItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/70"><Camera size={14} className="text-[#B8975A] mt-0.5 flex-shrink-0" /> {item}</li>
                ))}
              </ul>
              <h4 className="font-serif text-lg text-[#B8975A] mb-4">{t.medPostTitle}</h4>
              <ul className="space-y-2">
                {t.medPostItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/70"><Pill size={14} className="text-[#B8975A] mt-0.5 flex-shrink-0" /> {item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white/5 border border-[#B8975A]/30 p-6">
              <h4 className="font-serif text-lg text-[#B8975A] mb-4">{t.avoidTitle}</h4>
              <ul className="space-y-2">
                {t.avoidItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/70"><span className="text-red-400 mt-0.5 flex-shrink-0">✕</span> {item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white/5 border border-[#B8975A]/30 p-6">
              <h4 className="font-serif text-lg text-[#B8975A] mb-4">{t.scarTitle}</h4>
              <ul className="space-y-2">
                {t.scarItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/70"><Scissors size={14} className="text-[#B8975A] mt-0.5 flex-shrink-0" /> {item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Alert */}
      <section className="py-12 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="bg-red-50 border border-red-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={20} className="text-red-500" />
              <h4 className="font-serif text-lg text-red-700">{t.alertTitle}</h4>
            </div>
            <ul className="space-y-2">
              {t.alertItems.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-red-600">
                  <span className="mt-0.5">•</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">{t.ctaTitle}</h2>
          <p className="text-white/70 mb-10">{t.ctaDesc}</p>
          <Link to="/contacto" className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
            {t.requestConsult} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Braquioplastia;
